import { ApolloConsumer } from "@apollo/client";
import React, { useState, useMemo, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import momentTimezone from "moment-timezone";
import NoDataComponent from "./../../../components/Table/TableComponents/NoDataComponent";
import { NotificationContext } from "../NotificationContext";
import { ArchiveButton } from "./ArchiveButton";

import { GET_PAGINATED_NOTIFICATIONS } from "../../../screens/Notification/queries";

import { NotificationCounter } from "./styles";
import { ReactTable } from "../../SimpleForm/ReactTable";

const sortColumnResolver = (id) => {
  const resolve = {
    caseNumber: "caseNumber",
    notificationDate: "notificationDate",
    notificationMessage: "notificationMessage",
    visaType: "visaType",
    empBusinessName: "empBusinessName",
    jobTitle: "jobTitle",
    formId: "formId",
  };

  return resolve[id];
};

function NotificationTable(props) {
  const { navCounts, apolloClient, refetch } = props;
  const queryResolver = (type) => {
    const resolver = {
      Active: GET_PAGINATED_NOTIFICATIONS,
      Archived: GET_PAGINATED_NOTIFICATIONS,
    };
    return resolver[type];
  };
  const [selectedAll, setSelectedAll] = useState(false);
  const {
    activeNotificationNav,
    setCheckedNotifications,
    checkedNotifications,
  } = useContext(NotificationContext);
  const tabCounts = navCounts[`${activeNotificationNav.machine_name}`];
  const count = 0;

  let table_type = activeNotificationNav.machine_name;
  let query = queryResolver(table_type);

  const handleSelectAll = (e, all) => {
    const isChecked = e?.currentTarget?.checked;
    const rows = all?.data || [];
    setSelectedAll(isChecked);
    if (isChecked) {
      const newCheckedNotifications = rows.map(
        ({ partitionKey, notificationId }) => ({
          partitionKey,
          notificationId,
        })
      );
      return setCheckedNotifications([...newCheckedNotifications]);
    }
    setCheckedNotifications([]);
  };
  const handleSelect = (e, notification) => {
    const isChecked = e?.currentTarget?.checked;
    if (isChecked) {
      return setCheckedNotifications([...checkedNotifications, notification]);
    }
    const newCheckedNotifications = checkedNotifications.filter(
      (n) => n.notificationId !== notification.notificationId
    );
    setCheckedNotifications(newCheckedNotifications);
  };

  const columns = useMemo(
    () => [
      {
        show: table_type === "Active",
        Header: (all) => {
          if (navCounts.Active > 0) {
            return (
              <>
                <input
                  type="checkbox"
                  name="pwd-selection"
                  className="usa-checkbox__input"
                  id={`allListedNotifications`}
                  title="Select All"
                  onChange={(e) => e}
                  checked={selectedAll}
                  onClick={(e) => handleSelectAll(e, all)}
                  value={""}
                />
                <label
                  className="usa-checkbox__label"
                  style={{ padding: 0, margin: "0 -3px" }}
                  htmlFor={`allListedNotifications`}
                  onChange={(e) => e}
                  checked={selectedAll}
                />
              </>
            );
          }
          return null;
        },
        id: "checkbox", // It needs an ID
        fixedWidth: 40,
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          const { notificationId, partitionKey } = original;
          const checked =
            checkedNotifications &&
            checkedNotifications.some((n) => {
              return n.notificationId === original.notificationId;
            });
          return (
            <>
              <input
                type="checkbox"
                name="pwd-selection"
                className="usa-checkbox__input"
                id={`${notificationId}`}
                title={notificationId}
                onChange={(e) => e}
                checked={checked}
                onClick={(e) =>
                  handleSelect(e, { notificationId, partitionKey })
                }
                value={notificationId}
              />
              <label
                className="usa-checkbox__label"
                htmlFor={`${notificationId}`}
                onChange={(e) => e}
                style={{
                  padding: 0,
                  margin: "0 0 0 8px",
                }}
                checked={checked}
              />
            </>
          );
        },
      },

      {
        Header: "Date",
        accessor: "notificationDate",
        id: "notificationDate",
        fixedWidth: 110,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => {
          value = Number(value);
          const _date = momentTimezone(value)
            .tz("America/New_York")
            .format("MM/DD/YYYY");
          return <>{_date || "--"}</>;
        },
      },
      {
        Header: "Case Number",
        accessor: "caseNumber",
        id: "caseNumber",
        fixedWidth: 210,
        Cell: ({ cell: { value, row } }) => (
          <>
            {row.original.formId && (
              <Link
                to={{
                  pathname: `/dashboard/case/${row.original.formId}/${value}`,
                  state: {
                    prevPageTitle: "Notifications",
                    prevPageUrl: window.location.pathname,
                  },
                }}
                rel="noopener noreferrer"
              >
                <u>
                  <a className="usa-link">{value || "--"}</a>
                </u>
              </Link>
            )}
          </>
        ),
      },
      {
        Header: "Visa Type",
        accessor: "visaType",
        id: "visaType",
        fixedWidth: 100,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => <>{value || "--"}</>,
      },
      {
        Header: "Employer Name",
        accessor: "empBusinessName",
        id: "empBusinessName",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`empBusinessName_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`empBusinessName_${index}`}
            >
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Job Title",
        accessor: "jobTitle",
        id: "jobTitle",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => <>{value || "--"}</>,
      },
      {
        Header: "Notification",
        accessor: "notificationMessage",
        id: "notificationMessage",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`notificationMessage_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`notificationMessage_${index}`}
            >
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
    ],
    [table_type, checkedNotifications]
  );

  // const [allExpanded, setAllExpanded] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(count);
  const [pageCount, setPageCount] = useState(0);
  const [paginationProps, setPaginationProps] = useState({
    pageIndex: 0,
    pageSize: 0,
  });

  const renderId = useRef(0);
  const forceRefetch = () => {
    renderId.current++;
  };

  const NoDataConst = (props) => (
    <NoDataComponent noDataResult="There is no data for " {...props} />
  );

  const fetchData = async ({ pageSize, pageIndex, sortBy }) => {
    setLoading(true);
    setCheckedNotifications([]);
    setSelectedAll(false);
    let sort_ascending = 0;
    let sort_column = "notificationDate";
    let sort_object = sortBy[0]; // multi-sort disabled

    switch (table_type) {
      case "Active":
      case "Archived":
        if (!sort_object) {
          sort_object = { id: "notificationDate", desc: true };
        }
        break;
      default:
    }

    if (sort_object) {
      sort_column = sortColumnResolver(sort_object["id"]);
      sort_ascending = !sort_object["desc"] ? 1 : -1;
    }

    let variables = {
      UISorting: {
        tab: table_type,
        limit: pageSize,
        page: pageIndex,
        sort_ascending,
        sort_column,
      },
    };

    await apolloClient
      .watchQuery({
        query,
        variables,
        pollInterval: 0,
        fetchPolicy: "network-only",
      })
      .subscribe(({ data, error, loading }) => {
        if (error) {
          console.error(error);
        }

        if (!loading && data) {
          const gql_data = data[`getPaginatedNotifications`][`notifications`];
          const new_page_count =
            data[`getPaginatedNotifications`]["total_count"];
          // const which_count = variables.UISorting.search_text !== "" ? new_page_count : count;

          setData(gql_data);
          setTotalCount(new_page_count);
          setPageCount(Math.ceil(new_page_count / pageSize));
          setLoading(false);
        }
      });
  };

  const { pageIndex, pageSize } = paginationProps;
  let paginationRangeStart = pageIndex * pageSize + 1;
  let paginationRangeEnd = Math.min((pageIndex + 1) * pageSize, totalCount);

  return (
    <>
      <NotificationCounter>
        {`${isNaN(paginationRangeStart) ? 1 : paginationRangeStart} - ${
          isNaN(paginationRangeEnd) ? 1 : paginationRangeEnd
        } of ${totalCount ?? 0} ${table_type} Notifications`}
      </NotificationCounter>
      <ReactTable
        columns={columns}
        data={data}
        pageSizeOptions={[10, 25, 50, 100]}
        fetchData={fetchData}
        pageCount={pageCount}
        loading={loading}
        NoDataComponent={NoDataConst}
        tableTheme="casesTableTheme"
        tableStyle={{ minWidth: "1000px" }}
        allowUnsortedState={true}
        getPaginationProps={setPaginationProps}
        refetchDependencies={[renderId.current]}
        refetchToFirstPageDependencies={[table_type]}
      />
      <ArchiveButton
        show={table_type}
        data={checkedNotifications}
        refetch={refetch}
        forceRefetch={forceRefetch}
        setCheckedCases={setCheckedNotifications}
        clearCheckAll={setSelectedAll}
      />
    </>
  );
}

export default (props) => (
  <ApolloConsumer>
    {(client) => <NotificationTable apolloClient={client} {...props} />}
  </ApolloConsumer>
);
